import { useEffect, useMemo, useState } from "react";
import {
  fetchEmailCampaigns,
  fetchEmailCatalog,
  fetchEmailRecipientCount,
  sendEmailCampaign,
} from "../api";

function formatDate(value) {
  try {
    return new Intl.DateTimeFormat("hu-HU", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function defaultCampaignName(templateLabel) {
  const date = new Intl.DateTimeFormat("hu-HU", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date());
  return `${templateLabel} — ${date}`;
}

function EmailCampaigns({ token, adminUsername }) {
  const [catalog, setCatalog] = useState([]);
  const [emailType, setEmailType] = useState("");
  const [langFilter, setLangFilter] = useState("all");
  const [campaignName, setCampaignName] = useState("");
  const [recipientCount, setRecipientCount] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const sendableTemplates = useMemo(
    () => catalog.filter((item) => item.sendSupported),
    [catalog],
  );

  const selectedTemplate = useMemo(
    () => sendableTemplates.find((item) => item.id === emailType),
    [sendableTemplates, emailType],
  );

  async function loadCampaigns() {
    const data = await fetchEmailCampaigns(token);
    setCampaigns(data.campaigns || []);
  }

  async function loadRecipientCount(filter) {
    const data = await fetchEmailRecipientCount(token, filter);
    setRecipientCount(data.count);
  }

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    async function init() {
      setLoading(true);
      setError("");

      try {
        const catalogData = await fetchEmailCatalog(token);
        if (cancelled) return;

        const templates = catalogData.templates || [];
        setCatalog(templates);

        const firstSendable = templates.find((item) => item.sendSupported);
        if (firstSendable) {
          setEmailType(firstSendable.id);
        }

        await Promise.all([loadCampaigns(), loadRecipientCount("all")]);
      } catch {
        if (!cancelled) setError("Nem sikerült betölteni az adatokat.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!token) return;
    loadRecipientCount(langFilter).catch(() => {
      setRecipientCount(null);
    });
  }, [token, langFilter]);

  async function handleSend() {
    if (!emailType || recipientCount === 0) return;

    const name =
      campaignName.trim() ||
      defaultCampaignName(selectedTemplate?.label || emailType);

    const confirmed = window.confirm(
      `Küldés ${recipientCount} címzettnek?\n\nSablon: ${selectedTemplate?.label || emailType}\nKampány: ${name}`,
    );
    if (!confirmed) return;

    setSending(true);
    setError("");
    setResult(null);

    try {
      const data = await sendEmailCampaign(token, {
        emailType,
        campaignName: name,
        langFilter,
        sentBy: adminUsername || "",
      });
      setResult(data);
      await loadCampaigns();
    } catch {
      setError("A kampány küldése sikertelen.");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <header className="admin-dash-header">
        <div>
          <h1 className="admin-dash-title">Kampányok</h1>
          <p className="admin-dash-subtitle">
            Tömeges e-mail küldés az összes feliratkozónak.
          </p>
        </div>
      </header>

      <section className="admin-email-campaign-form">
        {loading ? (
          <p className="admin-dash-empty">Betöltés…</p>
        ) : (
          <>
            <p className="admin-email-recipient-count">
              Összes feliratkozó ({recipientCount ?? "…"} címzett)
            </p>

            <div className="admin-email-campaign-fields">
              <label className="admin-email-field">
                <span>Sablon</span>
                <select
                  value={emailType}
                  onChange={(e) => setEmailType(e.target.value)}
                >
                  {sendableTemplates.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="admin-email-field">
                <span>Nyelv szűrő</span>
                <select
                  value={langFilter}
                  onChange={(e) => setLangFilter(e.target.value)}
                >
                  <option value="all">Összes</option>
                  <option value="hu">Csak HU</option>
                  <option value="en">Csak EN</option>
                </select>
              </label>

              <label className="admin-email-field admin-email-field-wide">
                <span>Kampány neve (opcionális)</span>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder={
                    selectedTemplate
                      ? defaultCampaignName(selectedTemplate.label)
                      : "Kampány neve"
                  }
                />
              </label>
            </div>

            <button
              type="button"
              className="admin-dash-btn admin-dash-btn-primary"
              onClick={handleSend}
              disabled={sending || !emailType || recipientCount === 0}
            >
              {sending ? "Küldés folyamatban…" : "Küldés a listára"}
            </button>

            {error && (
              <p className="admin-email-error" role="alert">
                {error}
              </p>
            )}

            {result && (
              <div className="admin-email-result" role="status">
                <p>
                  <strong>Kész.</strong> Összesen: {result.total ?? result.totalRecipients}, sikeres:{" "}
                  {result.sent}, sikertelen: {result.failed}
                </p>
                {result.errors?.length > 0 && (
                  <details className="admin-email-result-errors">
                    <summary>Hibák ({result.errors.length})</summary>
                    <ul>
                      {result.errors.map((entry) => (
                        <li key={entry.email}>
                          {entry.email}: {entry.error}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </>
        )}
      </section>

      <section className="admin-dash-table-card admin-email-campaign-history">
        <h2 className="admin-email-panel-title">Korábbi kampányok</h2>
        {campaigns.length === 0 ? (
          <p className="admin-dash-empty">Még nincs kampány.</p>
        ) : (
          <div className="admin-dash-table-wrap">
            <table className="admin-dash-table">
              <thead>
                <tr>
                  <th>Név</th>
                  <th>Sablon</th>
                  <th>Szűrő</th>
                  <th>Állapot</th>
                  <th>Eredmény</th>
                  <th>Küldte</th>
                  <th>Dátum</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.name}</td>
                    <td>{entry.emailType}</td>
                    <td>{entry.langFilter?.toUpperCase() || "ALL"}</td>
                    <td>
                      <span
                        className={`admin-email-status admin-email-status-${entry.status}`}
                      >
                        {entry.status}
                      </span>
                    </td>
                    <td>
                      {entry.sent}/{entry.totalRecipients}
                      {entry.failed > 0 && ` (${entry.failed} hiba)`}
                    </td>
                    <td>{entry.sentBy || "—"}</td>
                    <td>{formatDate(entry.completedAt || entry.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export default EmailCampaigns;
